import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import jsQR from "jsqr";
import { useAuth } from "../context/authContext";
import { removeOnlyKeys } from "../utils/session";
import { getServicioStyle } from "../utils/servicioColores";
import CampanaNotificaciones from "../components/CampanaNotificaciones";

const DIAS_SEMANA = [
  { key: "l", label: "Lunes" },
  { key: "m", label: "Martes" },
  { key: "x", label: "Miércoles" },
  { key: "j", label: "Jueves" },
  { key: "v", label: "Viernes" },
  { key: "s", label: "Sábado" },
];

const DIA_HOY_KEY = ["d","l","m","x","j","v","s"][new Date().getDay()];

const ESTADO = {
  sin_plan:  { bg: "#f3f4f6", color: "#6b7280", borde: "#d1d5db", label: "Sin planificar", clickable: false },
  pendiente: { bg: "#fef9c3", color: "#92400e", borde: "#f59e0b", label: "Por hacer",      clickable: true  },
  en_curso:  { bg: "#dbeafe", color: "#1e3a8a", borde: "#3b82f6", label: "En curso",       clickable: true  },
  cerrado:   { bg: "#dcfce7", color: "#14532d", borde: "#22c55e", label: "✅ Cerrado",      clickable: true  },
  hecho:     { bg: "#dcfce7", color: "#14532d", borde: "#22c55e", label: "✅ Hecho",        clickable: true  },
};

const ICONO_MAP = { sin_plan: "⬜", pendiente: "🟡", en_curso: "🔵", cerrado: "🟢", hecho: "🟢" };

// Sustituciones (vienen del Sheet): "2026-10-02" -> "02/10"
const ddmm = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");
const rangoFechas = (i, f) => (!f ? `desde el ${ddmm(i)}` : i === f ? `el ${ddmm(i)}` : `del ${ddmm(i)} al ${ddmm(f)}`);
const hhmm = (t) => (t ? String(t).slice(0, 5) : null);
const horasTexto = (t) => {
  if (!t) return null;
  const [h, m] = String(t).split(":").map(Number);
  return m ? `${h}h ${m}min` : `${h}h`;
};

// CartoCiudad (IGN, oficial España) → "CALLE Iturrama, 12 · 31007 Pamplona"
// Campos: tip_via, address (nombre vía), portalNumber, postalCode, muni/poblacion, state
const formatearCartoCiudad = (d) => {
  if (!d || !d.address) return "";
  const tipo = d.tip_via ? `${d.tip_via} ` : "";
  const num = (d.portalNumber && d.portalNumber > 0) ? `, ${d.portalNumber}` : "";
  const cp = d.postalCode ? `${d.postalCode} ` : "";
  const muni = d.muni || d.poblacion || "";
  const linea1 = `${tipo}${d.address}${num}`.trim();
  const linea2 = `${cp}${muni}`.trim();
  return [linea1, linea2].filter(Boolean).join(" · ");
};

// Nominatim (OSM, fallback) → "Calle X, 12 · 31008 Pamplona"
const formatearDireccion = (addr) => {
  if (!addr) return "";
  const calle = addr.road || addr.pedestrian || addr.footway || addr.neighbourhood || "";
  const portal = addr.house_number ? `, ${addr.house_number}` : "";
  const cp = addr.postcode ? `${addr.postcode} ` : "";
  const ciudad = addr.city || addr.town || addr.village || addr.municipality || "";
  const linea1 = calle ? `${calle}${portal}` : "";
  const linea2 = `${cp}${ciudad}`.trim();
  return [linea1, linea2].filter(Boolean).join(" · ");
};

const esIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

// Componente reutilizable para el bloque de error de geolocalización
const GeoError = ({ msg, esPermiso, onReintentar }) => (
  <div style={{
    background: "#fef2f2", border: "1.5px solid #fca5a5", borderRadius: 14,
    padding: "14px 16px", color: "#991b1b",
  }}>
    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: esPermiso ? 10 : 0 }}>
      ⚠️ {msg}
    </div>
    {esPermiso && esIOS() && (
      <div style={{ fontSize: 14, color: "#7f1d1d", lineHeight: 1.6 }}>
        <strong>Cómo activarlo en iPhone:</strong><br />
        1. Cierra Safari<br />
        2. Ve a <strong>Ajustes → Privacidad y seguridad → Localización → Safari</strong><br />
        3. Selecciona <em>Al usar la app</em><br />
        4. Vuelve a abrir esta página y pulsa Reintentar
      </div>
    )}
    {esPermiso && !esIOS() && (
      <div style={{ fontSize: 14, color: "#7f1d1d", lineHeight: 1.6 }}>
        Toca el icono 🔒 o ℹ️ en la barra de direcciones → <strong>Permisos del sitio → Ubicación → Permitir</strong>
      </div>
    )}
    <button style={{
      marginTop: 12, background: "#1e3a5f", color: "#fff", border: "none",
      borderRadius: 10, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer",
    }} onClick={onReintentar}>
      🔄 Reintentar
    </button>
  </div>
);

const Asistenta = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pisos, setPisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalHorario, setModalHorario] = useState(false);
  const [horarioSemanal, setHorarioSemanal] = useState([]);
  // Piloto temporal: mientras esté activo, pulsar un piso lleva al reporte diario en vez de a ejecución
  const [modoReporte, setModoReporte] = useState("servicio");   // servicio | reporte | ambos
  const reporteActivo = modoReporte === "reporte";
  const ambos = modoReporte === "ambos";

  // ── Fichar (QR + ubicación) ──────────────────────────────
  const [modalFichar, setModalFichar] = useState(false);
  const [scanResult, setScanResult] = useState(null);   // { texto, hora }
  const [camError, setCamError] = useState("");
  // geo.direccion: { status: "idle"|"loading"|"ok"|"error", texto, raw }
  const [geo, setGeo] = useState({
    status: "idle", coords: null, error: "", esPermiso: false,
    direccion: { status: "idle", texto: "", raw: null, aprox: false },
  });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/api/servicio/mis_pisos_hoy");
      setPisos(Array.isArray(res.data) ? res.data : []);
    } catch {
      setError("No se pudieron cargar los pisos de hoy.");
    } finally {
      setLoading(false);
    }
  }, []);

  const abrirHorario = async () => {
    try {
      const res = await axios.get("/api/servicio/horario_semanal");
      setHorarioSemanal(Array.isArray(res.data) ? res.data : []);
    } catch {
      setHorarioSemanal([]);
    }
    // Añadir entrada en historial para interceptar el botón atrás
    window.history.pushState({ horarioModal: true }, "");
    setModalHorario(true);
  };

  const cerrarHorario = () => {
    setModalHorario(false);
    // Si la entrada dummy sigue en el historial, quitarla
    if (window.history.state?.horarioModal) window.history.back();
  };

  const abrirCalendarioAnual = async () => {
    // Se abre la pestaña antes del await para que el navegador no la bloquee como popup.
    const ventana = window.open("", "_blank");
    try {
      const res = await axios.get("/api/calendario-anual-asistentas.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (ventana) ventana.location.href = url;
      else window.open(url, "_blank");
    } catch {
      if (ventana) ventana.close();
      window.alert("No se pudo abrir el calendario anual.");
    }
  };

  useEffect(() => {
    const onPopState = (e) => {
      if (modalHorario) {
        setModalHorario(false);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [modalHorario]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    axios.get("/api/servicio/config/reporte_asistenta")
      .then((res) => setModoReporte(res.data?.modo || (res.data?.activo ? "reporte" : "servicio")))
      .catch(() => setModoReporte("servicio"));
  }, []);

  useEffect(() => {
    const onFocus = () => cargar();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [cargar]);

  // ── Reverse geocoding (calle + portal) ───────────────────
  // 1º CartoCiudad (oficial España, trae portal) · 2º Nominatim (OSM) como fallback
  const reverseGeocode = useCallback(async (lat, lng) => {
    setGeo((g) => ({ ...g, direccion: { status: "loading", texto: "", raw: null, aprox: false } }));

    // 1) CartoCiudad
    try {
      const url = `https://www.cartociudad.es/geocoder/api/geocoder/reverseGeocode?lon=${lng}&lat=${lat}`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (res.ok) {
        const d = await res.json();
        const texto = formatearCartoCiudad(d);
        if (texto) {
          // state !== 1 → no encontró el portal exacto y lo ajustó al más próximo
          const aprox = (d.state && d.state !== 1) || !(d.portalNumber > 0);
          setGeo((g) => ({ ...g, direccion: { status: "ok", texto, raw: d, aprox } }));
          return;
        }
      }
    } catch {
      // CORS o error de red → seguimos al fallback
    }

    // 2) Nominatim (OSM)
    try {
      const url =
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2` +
        `&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1&accept-language=es`;
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error("HTTP " + res.status);
      const data = await res.json();
      const texto = formatearDireccion(data.address) || data.display_name || "";
      setGeo((g) => ({
        ...g,
        direccion: { status: texto ? "ok" : "error", texto, raw: data.address || null, aprox: !data.address?.house_number },
      }));
    } catch {
      setGeo((g) => ({ ...g, direccion: { status: "error", texto: "", raw: null, aprox: false } }));
    }
  }, []);

  // ── Geolocalización ──────────────────────────────────────
  // Safari iOS exige que getCurrentPosition se llame lo más inmediato posible
  // al gesto del usuario (tap). Por eso separamos "disparar la petición" de
  // "actualizar el estado": el dispatch ocurre antes de cualquier setState.
  const _dispararGeo = useCallback(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setGeo((g) => ({
          ...g,
          status: "ok",
          coords: { lat, lng, acc: Math.round(pos.coords.accuracy) },
          error: "", esPermiso: false,
        }));
        reverseGeocode(lat, lng);
      },
      (err) => {
        let msg = "No se pudo obtener la ubicación.";
        let esPermiso = false;
        if (err.code === 1) {
          esPermiso = true;
          msg = esIOS()
            ? "Permiso de ubicación denegado por iOS."
            : "Permiso de ubicación denegado.";
        } else if (err.code === 2) {
          msg = "Ubicación no disponible. Comprueba que el GPS esté activado.";
        } else if (err.code === 3) {
          msg = "Se agotó el tiempo de espera. Comprueba tu señal GPS e inténtalo de nuevo.";
        }
        setGeo((g) => ({ ...g, status: "error", coords: null, error: msg, esPermiso }));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [reverseGeocode]);

  // Versión "pública" que también resetea el estado visual (para el botón Reintentar)
  const pedirUbicacion = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGeo((g) => ({ ...g, status: "error", coords: null, error: "Este dispositivo no soporta geolocalización.", esPermiso: false }));
      return;
    }
    setGeo((g) => ({ ...g, status: "loading", coords: null, error: "", esPermiso: false, direccion: { status: "idle", texto: "", raw: null, aprox: false } }));
    _dispararGeo();
  }, [_dispararGeo]);

  // ── Cámara + escaneo QR ──────────────────────────────────
  const pararCamara = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    // Arranca la cámara cuando el modal está abierto y aún no hay resultado
    if (!modalFichar || scanResult) return;
    let cancelado = false;

    const tick = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (cancelado || !video || !canvas) return;
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
        if (code && code.data) {
          if (navigator.vibrate) navigator.vibrate(120);
          setScanResult({ texto: code.data, hora: new Date() });
          return; // el cleanup del effect parará la cámara
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const iniciar = async () => {
      setCamError("");
      if (!navigator.mediaDevices?.getUserMedia) {
        setCamError("Este navegador no permite acceder a la cámara (se requiere HTTPS).");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelado) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.setAttribute("playsinline", "true"); // necesario en iOS
        await video.play();
        rafRef.current = requestAnimationFrame(tick);
      } catch (e) {
        let msg = "No se pudo acceder a la cámara.";
        if (e?.name === "NotAllowedError") msg = "Permiso de cámara denegado. Actívalo en los ajustes del navegador para este sitio.";
        else if (e?.name === "NotFoundError") msg = "No se ha encontrado ninguna cámara en el dispositivo.";
        else if (e?.name === "NotReadableError") msg = "La cámara está siendo usada por otra aplicación.";
        setCamError(msg);
      }
    };

    iniciar();
    return () => {
      cancelado = true;
      pararCamara();
    };
  }, [modalFichar, scanResult, pararCamara]);

  const abrirFichar = () => {
    // ⚠️ iOS Safari: getCurrentPosition DEBE ser lo primero que ocurra en el handler
    // del tap. Cualquier setState previo puede romper el "user gesture" y no mostrar
    // el diálogo de permisos. Por eso disparamos la geo aquí, sincrónicamente,
    // antes de tocar ningún estado.
    if ("geolocation" in navigator) {
      setGeo((g) => ({ ...g, status: "loading", coords: null, error: "", esPermiso: false, direccion: { status: "idle", texto: "", raw: null, aprox: false } }));
      _dispararGeo();
    } else {
      setGeo((g) => ({ ...g, status: "error", error: "Este dispositivo no soporta geolocalización.", esPermiso: false }));
    }
    setScanResult(null);
    setCamError("");
    setModalFichar(true);
  };

  const cerrarFichar = () => {
    pararCamara();
    setModalFichar(false);
    setScanResult(null);
    setCamError("");
    setGeo({ status: "idle", coords: null, error: "", esPermiso: false, direccion: { status: "idle", texto: "", raw: null, aprox: false } });
  };

  const escanearOtro = () => {
    setScanResult(null);
    if (geo.status !== "ok") pedirUbicacion();
  };

  const handleLogout = () => {
    if (window.confirm("\u00bfCerrar sesi\u00f3n?")) {
      removeOnlyKeys(["user", "session_token", "access_token", "refresh_token"]);
      navigate("/login", { replace: true });
    }
  };

  const hoyStr = new Date().toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

  const esDomingo = new Date().getDay() === 0;

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <button style={s.btnHorario} onClick={abrirHorario}>📅 Horario semanal</button>
          <button style={s.btnSalir} onClick={handleLogout}>Salir</button>
        </div>
        <div style={{ marginBottom: 14 }}>
          <button style={s.btnHorario} onClick={abrirCalendarioAnual}>🗓️ Calendario anual</button>
        </div>
        <div>
          <div style={s.greeting}>Hola, {(() => { const n = user?.nombre || user?.user || ''; const i = n.indexOf(','); return i !== -1 ? n.slice(i + 1).trim() : n; })()} 👋</div>
          <div style={s.panelDe}>Panel de {user?.user}</div>
          <div style={s.fechaHoy}>{hoyStr}</div>
        </div>
        {/* Avisos de cambios en mis pisos: esquina inferior derecha del recuadro del nombre */}
        <div style={{ position: "absolute", right: 14, bottom: 14 }}>
          <CampanaNotificaciones rol="asistenta" usuario={user?.user} conActivarPush conPermisos estiloBoton={{ background: "rgba(255,255,255,0.92)", boxShadow: "0 3px 10px rgba(0,0,0,0.18)" }} />
        </div>
      </div>

      {/* Fichaje unificado (QR + ubicación + alertas): lo gestiona components/FichajePiso.js, el mismo que las supervisoras */}
      <button style={s.btnFichar} onPointerDown={() => window.dispatchEvent(new Event("fichaje:precalentar"))} onClick={() => window.dispatchEvent(new Event("fichaje:abrir"))}>
        📷 Fichar
      </button>

      <div style={s.titulo}>🏠 Mis pisos de hoy</div>

      {loading ? (
        <div style={s.centrado}>⏳ Cargando…</div>
      ) : error ? (
        <div style={s.errorBox}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
          <div>{error}</div>
          <button style={s.btnRecargar} onClick={cargar}>Reintentar</button>
        </div>
      ) : esDomingo ? (
        <div style={s.vacioBx}>
          <div style={{ fontSize: 80 }}>😴</div>
          <div style={s.vacioTitulo}>¡Hoy es domingo, descansa!</div>
        </div>
      ) : pisos.length === 0 ? (
        <div style={s.vacioBx}>
          <div style={{ fontSize: 80 }}>✅</div>
          <div style={s.vacioTitulo}>No tienes pisos hoy</div>
          <div style={s.vacioSub}>Si crees que es un error, habla con tu supervisora.</div>
        </div>
      ) : (
        <div style={s.lista}>
          {pisos.map(piso => {
            // Piso de una titular que está de baja (le sustituye otra asistenta):
            // se ve bloqueado y no hace nada al pulsarlo.
            if (piso.de_baja) {
              const b = piso.de_baja;
              return (
                <div key={piso.id} style={s.pisoCardBaja} aria-disabled="true">
                  <div style={{ ...s.pisoAbrev, background: "rgba(255,255,255,0.7)", color: "#64748b" }}>{piso.abreviatura}</div>
                  <div style={{ flex: 1, padding: "0 16px" }}>
                    <div style={s.badgeBaja}>🚫 DE BAJA</div>
                    <div style={{ fontSize: 15, color: "#475569", fontWeight: 700, marginTop: 8 }}>
                      Estás de baja {rangoFechas(b.fecha_inicio, b.fecha_fin)}
                    </div>
                    <div style={{ fontSize: 14, color: "#64748b", marginTop: 2 }}>Este piso lo hace otra compañera estos días.</div>
                  </div>
                  <div style={{ fontSize: 30, flexShrink: 0 }}>🔒</div>
                </div>
              );
            }
            if (ambos) {
              const eS = ESTADO[piso.estado] || ESTADO.sin_plan;
              const servHecho = piso.estado === "cerrado";
              const servOk = eS.clickable;
              const hor = (piso.hora_entrada || piso.hora_salida) ? `${(piso.hora_entrada || "--").slice(0, 5)} – ${(piso.hora_salida || "--").slice(0, 5)}` : null;
              const textoServ = servHecho ? "Hecho ✅ · toca para verlo" : piso.estado === "en_curso" ? "En curso · toca para seguir" : piso.estado === "pendiente" ? "Toca para empezar" : "Aún no está planificado";
              return (
                <div key={piso.id} style={s.pisoCardDoble}>
                  <div style={s.dobleCab}>
                    <div style={s.dobleAbrev}>{piso.abreviatura}</div>
                    <div style={{ flex: 1 }}>
                      {hor && <div style={{ fontSize: 24, fontWeight: 800, color: "#1e3a5f" }}>⏱ {hor}</div>}
                      {piso.servicio && (() => {
                        const sc = getServicioStyle(piso.servicio);
                        return <span style={{ ...s.badgeServicio, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`, marginTop: 4, display: "inline-block" }}>{piso.servicio}</span>;
                      })()}
                      {piso.sustitucion && <div style={s.badgeSustitucion}>🔁 Sustitución</div>}
                    </div>
                  </div>
                  <button type="button" disabled={!servOk}
                    onClick={() => navigate("/asistenta/piso", { state: { piso } })}
                    style={{ ...s.btnDoble, ...(servHecho ? s.btnDobleHecho : servOk ? s.btnDobleServicio : s.btnDobleOff) }}>
                    <span style={s.btnDobleIcono}>{servHecho ? "✅" : "🏠"}</span>
                    <span style={{ flex: 1, textAlign: "left" }}>
                      <span style={s.btnDobleTitulo}>1. Servicio del piso</span>
                      <span style={s.btnDobleSub}>{textoServ}</span>
                    </span>
                    {servOk && <span style={s.btnDobleFlecha}>›</span>}
                  </button>
                  <button type="button"
                    onClick={() => navigate(piso.reporte_hoy ? "/asistenta/verReporteDiario" : "/asistenta/reporteDiario", { state: { piso } })}
                    style={{ ...s.btnDoble, ...(piso.reporte_hoy ? s.btnDobleHecho : s.btnDobleReporte) }}>
                    <span style={s.btnDobleIcono}>{piso.reporte_hoy ? "✅" : "📝"}</span>
                    <span style={{ flex: 1, textAlign: "left" }}>
                      <span style={s.btnDobleTitulo}>2. Reporte del día</span>
                      <span style={s.btnDobleSub}>{piso.reporte_hoy ? "Enviado ✅ · toca para verlo" : "Toca para contar cómo ha ido"}</span>
                    </span>
                    <span style={s.btnDobleFlecha}>›</span>
                  </button>
                </div>
              );
            }
            // Con el piloto de reporte activo, "sin planificar" no aplica: se ve
            // y se comporta como "pendiente" (el reporte no depende de la planificación).
            // Si ya se envió el reporte de hoy, se ve como "hecho" sin importar lo anterior.
            const estadoVisual = piso.reporte_hoy
              ? "hecho"
              : (reporteActivo && piso.estado === "sin_plan") ? "pendiente" : piso.estado;
            const est = ESTADO[estadoVisual] || ESTADO.sin_plan;
            const icono = ICONO_MAP[estadoVisual] || "\u2b1c";
            const horario = (piso.hora_entrada || piso.hora_salida)
              ? `${(piso.hora_entrada || "--").slice(0, 5)} \u2013 ${(piso.hora_salida || "--").slice(0, 5)}`
              : null;
            // El reporte diario solo necesita piso.id, no depende de servicio_planificacion:
            // si el piloto está activo, el piso es accesible aunque no esté planificado.
            const clickable = reporteActivo || est.clickable;
            return (
              <div
                key={piso.id}
                style={{
                  ...s.pisoCard,
                  borderColor: est.borde,
                  background: est.bg,
                  cursor: clickable ? "pointer" : "default",
                  opacity: clickable ? 1 : 0.75,
                }}
                onClick={() => {
                  if (piso.reporte_hoy) { navigate("/asistenta/verReporteDiario", { state: { piso } }); return; }
                  if (reporteActivo) { navigate("/asistenta/reporteDiario", { state: { piso } }); return; }
                  if (!est.clickable) return;
                  navigate("/asistenta/piso", { state: { piso } });
                }}
              >
                <div style={s.pisoAbrev}>
                  {piso.abreviatura}
                  {piso.servicio && (() => {
                    const sc = getServicioStyle(piso.servicio);
                    return (
                      <div style={{ marginTop: 6 }}>
                        <span style={{ ...s.badgeServicio, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                          {piso.servicio}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                <div style={{ flex: 1, padding: "0 16px" }}>
                  {piso.sustitucion && (
                    <div style={s.badgeSustitucion}>
                      🔁 Sustitución
                    </div>
                  )}
                  {horario && <div style={s.pisoHorario}>⏱ {horario}</div>}
                  {piso.sustitucion && (piso.sustitucion.hora_entrada || piso.sustitucion.horas) && (
                    <div style={{ fontSize: 14, color: "#6d28d9", fontWeight: 700, marginTop: 4 }}>
                      {piso.sustitucion.hora_entrada && `Tu horario de sustitución: ${hhmm(piso.sustitucion.hora_entrada)}${piso.sustitucion.hora_salida ? ` – ${hhmm(piso.sustitucion.hora_salida)}` : ""}`}
                      {piso.sustitucion.horas && `${piso.sustitucion.hora_entrada ? " · " : ""}${horasTexto(piso.sustitucion.horas)}`}
                    </div>
                  )}
                  <div style={{ ...s.estadoBadge, color: est.color, marginTop: 8 }}>
                    {icono} {est.label}
                  </div>
                </div>
                {clickable && <div style={s.pisoFlecha}>›</div>}
              </div>
            );
          })}
        </div>
      )}

      {!loading && (
        <button style={s.btnRecargar} onClick={cargar}>🔄 Actualizar</button>
      )}

      {/* Modal horario semanal */}
      {modalHorario && (
        <div style={s.overlayHorario} onClick={cerrarHorario}>
          <div style={s.modalHorario} onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <span style={{ fontSize: 20, fontWeight: 900, color: "#1e3a5f" }}>📅 Mi horario semanal</span>
              <button onClick={cerrarHorario} style={s.btnCerrarModal}>✕</button>
            </div>
            {DIAS_SEMANA.map(({ key, label }) => {
              const pisosDelDia = horarioSemanal.filter(p => (p[`${key}_e`] || p[`${key}_s`]) && (!p.sustitucion_dias || p.sustitucion_dias.includes(key)));
              const esHoy = key === DIA_HOY_KEY;
              return (
                <div key={key} style={{ marginBottom: 14 }}>
                  <div style={{ ...s.diaHeader, background: esHoy ? "#ec4899" : "#1e3a5f" }}>
                    {label} {esHoy && <span style={{ fontSize: 12, marginLeft: 6, background: "rgba(255,255,255,0.3)", borderRadius: 6, padding: "1px 7px" }}>HOY</span>}
                  </div>
                  {pisosDelDia.length === 0 ? (
                    <div style={s.diaVacio}>— Libre —</div>
                  ) : (
                    [...pisosDelDia].sort((a, b) => (a[`${key}_e`] || "99:99").localeCompare(b[`${key}_e`] || "99:99")).map(p => (
                      <div key={p.id} style={{
                        ...s.pisoHorarioRow,
                        ...(p.baja_dias?.includes(key) ? { background: "repeating-linear-gradient(135deg,#f1f5f9 0 10px,#e2e8f0 10px 20px)", opacity: 0.85 } : {}),
                        ...(p.sustitucion_dias ? { background: "#f5f3ff", border: "1px solid #c4b5fd" } : {}),
                      }}>
                        <span style={s.pisoHorarioAbrev}>
                          {p.abreviatura}
                          {p.baja_dias?.includes(key) && <span style={{ ...s.badgeBaja, fontSize: 11, padding: "2px 8px", marginLeft: 8, verticalAlign: "middle" }}>DE BAJA</span>}
                          {p.sustitucion_dias && <span style={{ ...s.badgeSustitucion, fontSize: 11, padding: "2px 8px", marginLeft: 8, verticalAlign: "middle", marginBottom: 0 }}>🔁 Sustitución</span>}
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {p.servicio && (() => {
                            const sc = getServicioStyle(p.servicio);
                            return (
                              <span style={{ ...s.badgeServicioSmall, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                                {p.servicio}
                              </span>
                            );
                          })()}
                          <span style={s.pisoHorarioHora}>
                            {(p[`${key}_e`] || "--").slice(0, 5)} – {(p[`${key}_s`] || "--").slice(0, 5)}
                          </span>
                        </span>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal fichar (QR + ubicación) */}
      {modalFichar && (
        <div style={s.overlayFichar}>
          <div style={s.modalFichar}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span style={{ fontSize: 20, fontWeight: 900, color: "#1e3a5f" }}>
                {scanResult ? "✅ QR escaneado" : "📷 Escanea el QR del piso"}
              </span>
              <button onClick={cerrarFichar} style={s.btnCerrarModal}>✕</button>
            </div>

            {!scanResult ? (
              <>
                {camError ? (
                  <div style={s.ficharErrorBox}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>🚫</div>
                    <div>{camError}</div>
                  </div>
                ) : (
                  <div style={s.videoWrap}>
                    <video ref={videoRef} style={s.video} muted playsInline />
                    <div style={s.scanMarco} />
                  </div>
                )}
                <canvas ref={canvasRef} style={{ display: "none" }} />
                <div style={s.geoBar}>
                  {geo.status === "loading" && <span>📍 Obteniendo ubicación…</span>}
                  {geo.status === "ok" && (
                    <span style={{ color: "#14532d" }}>
                      📍 Ubicación lista (±{geo.coords.acc} m)
                      {geo.direccion.status === "loading" && <div style={s.geoCalleSub}>Buscando calle…</div>}
                      {geo.direccion.status === "ok" && <div style={s.geoCalleSub}>{geo.direccion.texto}</div>}
                    </span>
                  )}
                  {geo.status === "error" && (
                    <GeoError
                      msg={geo.error}
                      esPermiso={geo.esPermiso}
                      onReintentar={pedirUbicacion}
                    />
                  )}
                </div>
              </>
            ) : (
              <div>
                <div style={s.resultadoBox}>
                  <div style={s.resultadoLabel}>Contenido del QR</div>
                  <div style={s.resultadoTexto}>{scanResult.texto}</div>
                </div>

                <div style={s.resultadoBox}>
                  <div style={s.resultadoLabel}>Hora</div>
                  <div style={s.resultadoValor}>
                    {scanResult.hora.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                  </div>
                </div>

                <div style={s.resultadoBox}>
                  <div style={s.resultadoLabel}>Dirección del fichaje</div>
                  {geo.status === "ok" ? (
                    <div>
                      {geo.direccion.status === "ok" ? (
                        <div>
                          <div style={s.resultadoDireccion}>📌 {geo.direccion.texto}</div>
                          {geo.direccion.aprox && (
                            <div style={s.avisoAprox}>⚠️ Portal aproximado (ajustado al más cercano)</div>
                          )}
                        </div>
                      ) : geo.direccion.status === "loading" ? (
                        <div style={s.resultadoValor}>⏳ Buscando la calle…</div>
                      ) : (
                        <div style={{ fontSize: 14, color: "#9ca3af" }}>No se pudo obtener la calle (solo coordenadas).</div>
                      )}
                      <div style={s.resultadoCoords}>
                        {geo.coords.lat.toFixed(6)}, {geo.coords.lng.toFixed(6)} · ±{geo.coords.acc} m
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${geo.coords.lat},${geo.coords.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={s.linkMapa}
                      >
                        🗺️ Ver en el mapa
                      </a>
                    </div>
                  ) : geo.status === "loading" ? (
                    <div style={s.resultadoValor}>⏳ Obteniendo ubicación…</div>
                  ) : (
                    <GeoError
                      msg={geo.error || "Sin ubicación."}
                      esPermiso={geo.esPermiso}
                      onReintentar={pedirUbicacion}
                    />
                  )}
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
                  <button style={s.btnSecundario} onClick={escanearOtro}>📷 Escanear otro</button>
                  <button style={s.btnPrimario} onClick={cerrarFichar}>Cerrar</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const s = {
  page: {
    maxWidth: 640, margin: "0 auto", padding: "16px 16px 80px",
    fontFamily: "'Segoe UI', sans-serif", minHeight: "100vh", background: "#f0f4f8",
  },
  header: {
    display: "flex", flexDirection: "column", position: "relative",
    background: "linear-gradient(135deg, #ec4899, #db2777)",
    borderRadius: 20, padding: "16px 18px 20px", marginBottom: 18,
    boxShadow: "0 6px 20px rgba(219,39,119,0.35)",
  },
  greeting: { fontSize: 24, fontWeight: 900, color: "#fff" },
  panelDe: { fontSize: 13, color: "rgba(255,255,255,0.7)", marginTop: 2 },
  fechaHoy: { fontSize: 15, color: "rgba(255,255,255,0.9)", marginTop: 4, textTransform: "capitalize" },
  btnSalir: {
    background: "rgba(255,255,255,0.25)", border: "2px solid rgba(255,255,255,0.5)",
    color: "#fff", borderRadius: 10, padding: "8px 16px", cursor: "pointer",
    fontWeight: 700, fontSize: 14, flexShrink: 0,
  },
  btnFichar: {
    display: "block", width: "100%",
    background: "linear-gradient(135deg, #1e3a5f, #2d4f7c)",
    border: "none", borderRadius: 18, padding: "20px",
    color: "#fff", fontSize: 22, fontWeight: 900, cursor: "pointer",
    boxShadow: "0 5px 18px rgba(30,58,95,0.4)",
    marginBottom: 24, letterSpacing: "0.3px",
  },
  titulo: { fontSize: 22, fontWeight: 900, color: "#1e3a5f", marginBottom: 16, paddingLeft: 4 },
  lista: { display: "flex", flexDirection: "column", gap: 14 },
  pisoCard: {
    display: "flex", alignItems: "center", padding: "22px 18px",
    borderRadius: 20, border: "3px solid", boxShadow: "0 3px 14px rgba(0,0,0,0.1)",
  },
  pisoAbrev: {
    fontSize: 15, fontWeight: 900, color: "#1e3a5f",
    minWidth: 90, textAlign: "center",
    background: "rgba(255,255,255,0.8)", borderRadius: 12,
    padding: "12px 8px", lineHeight: 1.4,
  },
  pisoHorario: { fontSize: 20, fontWeight: 800, color: "#1e3a5f" },
  pisoNombreAs: { fontSize: 16, color: "#4b5563", marginTop: 4, fontWeight: 600 },
  badgeServicio: {
    display: "inline-block", padding: "3px 12px", borderRadius: 10,
    fontSize: 14, fontWeight: 800, letterSpacing: "0.3px", marginTop: 8,
  },
  badgeServicioSmall: {
    display: "inline-block", padding: "2px 9px", borderRadius: 8,
    fontSize: 12, fontWeight: 800, letterSpacing: "0.2px",
  },
  estadoBadge: { fontSize: 17, fontWeight: 800 },
  pisoCardDoble: { padding: "16px 14px 18px", borderRadius: 22, border: "3px solid #cbd5e1", background: "#fff", boxShadow: "0 3px 14px rgba(0,0,0,0.1)", display: "flex", flexDirection: "column", gap: 12 },
  dobleCab: { display: "flex", alignItems: "center", gap: 14 },
  dobleAbrev: { fontSize: 26, fontWeight: 900, color: "#1e3a5f", background: "#e0e7ff", borderRadius: 16, padding: "16px 16px", minWidth: 120, textAlign: "center", lineHeight: 1.3 },
  btnDoble: { display: "flex", alignItems: "center", gap: 14, width: "100%", minHeight: 64, padding: "10px 14px", borderRadius: 16, border: "none", fontFamily: "inherit", cursor: "pointer", boxSizing: "border-box", boxShadow: "0 4px 0 rgba(0,0,0,0.18)" },
  btnDobleServicio: { background: "#2563eb", color: "#fff" },
  btnDobleReporte: { background: "#ea580c", color: "#fff" },
  btnDobleHecho: { background: "#16a34a", color: "#fff" },
  btnDobleOff: { background: "#e5e7eb", color: "#6b7280", cursor: "not-allowed", boxShadow: "none" },
  btnDobleIcono: { fontSize: 30, flexShrink: 0, lineHeight: 1 },
  btnDobleTitulo: { display: "block", fontSize: 18, fontWeight: 900, lineHeight: 1.2 },
  btnDobleSub: { display: "block", fontSize: 14, fontWeight: 600, marginTop: 3, opacity: 0.95 },
  btnDobleFlecha: { fontSize: 34, fontWeight: 300, lineHeight: 1, flexShrink: 0 },
  pisoCardBaja: {
    display: "flex", alignItems: "center", padding: "22px 18px",
    borderRadius: 20, border: "3px dashed #94a3b8", cursor: "not-allowed", userSelect: "none",
    background: "repeating-linear-gradient(135deg, #f8fafc 0 14px, #eef2f7 14px 28px)",
    boxShadow: "none", filter: "grayscale(0.4)",
  },
  badgeBaja: {
    display: "inline-block", background: "#475569", color: "#fff", fontWeight: 900, fontSize: 16,
    letterSpacing: 1.5, padding: "5px 14px", borderRadius: 999,
  },
  badgeSustitucion: {
    display: "inline-block", background: "#ede9fe", color: "#5b21b6", border: "1px solid #c4b5fd",
    fontWeight: 800, fontSize: 13.5, padding: "4px 10px", borderRadius: 999, marginBottom: 8,
  },
  pisoFlecha: { fontSize: 44, color: "#9ca3af", fontWeight: 300, flexShrink: 0, paddingLeft: 8, lineHeight: 1 },
  centrado: { textAlign: "center", color: "#555", marginTop: 60, fontSize: 20 },
  errorBox: {
    textAlign: "center", color: "#991b1b", background: "#fee2e2",
    borderRadius: 16, padding: "28px", fontSize: 18,
  },
  vacioBx: {
    textAlign: "center", padding: "60px 20px",
    background: "#fff", borderRadius: 20, border: "3px dashed #e5e7eb",
  },
  vacioTitulo: { fontSize: 22, fontWeight: 800, color: "#374151", marginTop: 16 },
  vacioSub: { fontSize: 16, color: "#9ca3af", marginTop: 8 },
  btnRecargar: {
    display: "block", margin: "28px auto 0",
    background: "#fff", border: "3px solid #e2e8f0", borderRadius: 14,
    padding: "16px 36px", fontSize: 18, fontWeight: 700, color: "#374151", cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
  },
  btnHorario: {
    background: "linear-gradient(135deg, #fff, #f0f4ff)",
    border: "2.5px solid rgba(255,255,255,0.8)",
    color: "#1e3a5f", borderRadius: 14, padding: "12px 18px", cursor: "pointer",
    fontWeight: 900, fontSize: 16, flexShrink: 0, whiteSpace: "nowrap",
    boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
    letterSpacing: "0.2px",
  },
  overlayHorario: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 1000,
  },
  modalHorario: {
    background: "#fff", borderRadius: "20px 20px 0 0",
    width: "100%", maxWidth: 560,
    padding: "24px 20px 40px", maxHeight: "82vh", overflowY: "auto",
    boxShadow: "0 -6px 24px rgba(0,0,0,0.2)",
  },
  btnCerrarModal: {
    background: "#f3f4f6", border: "none", borderRadius: 10,
    padding: "8px 14px", cursor: "pointer", fontSize: 18, color: "#6b7280", fontWeight: 700,
  },
  diaHeader: {
    color: "#fff", fontWeight: 800, fontSize: 15, borderRadius: 10,
    padding: "8px 14px", marginBottom: 6,
  },
  diaVacio: {
    fontSize: 14, color: "#9ca3af", padding: "6px 14px", fontStyle: "italic",
  },
  pisoHorarioRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
    padding: "10px 14px", marginBottom: 6,
  },
  pisoHorarioAbrev: { fontSize: 16, fontWeight: 900, color: "#1e3a5f" },
  pisoHorarioHora: { fontSize: 16, fontWeight: 700, color: "#374151" },

  // ── Estilos fichar ──
  overlayFichar: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100,
    padding: 16,
  },
  modalFichar: {
    background: "#fff", borderRadius: 20,
    width: "100%", maxWidth: 480,
    padding: "20px 18px 24px", maxHeight: "90vh", overflowY: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
  },
  videoWrap: {
    position: "relative", width: "100%", borderRadius: 16, overflow: "hidden",
    background: "#000", aspectRatio: "3 / 4",
  },
  video: {
    width: "100%", height: "100%", objectFit: "cover", display: "block",
  },
  scanMarco: {
    position: "absolute", top: "50%", left: "50%",
    transform: "translate(-50%, -50%)",
    width: "62%", aspectRatio: "1 / 1",
    border: "4px solid rgba(255,255,255,0.9)", borderRadius: 18,
    boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
    pointerEvents: "none",
  },
  geoBar: {
    marginTop: 14, fontSize: 17, fontWeight: 800, color: "#374151",
    background: "#f8fafc", border: "2px solid #cbd5e1", borderRadius: 14,
    padding: "14px 16px", textAlign: "center",
  },
  geoCalleSub: {
    fontSize: 22, fontWeight: 900, color: "#1e3a5f", marginTop: 8, lineHeight: 1.3,
  },
  btnGeoReintentar: {
    background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 8,
    padding: "5px 12px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginLeft: 6,
  },
  ficharErrorBox: {
    textAlign: "center", color: "#991b1b", background: "#fee2e2",
    borderRadius: 16, padding: "28px 18px", fontSize: 16, fontWeight: 600,
  },
  resultadoBox: {
    background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: "14px 16px", marginBottom: 12,
  },
  resultadoLabel: {
    fontSize: 12, fontWeight: 800, color: "#9ca3af", textTransform: "uppercase",
    letterSpacing: "0.6px", marginBottom: 6,
  },
  resultadoTexto: {
    fontSize: 28, fontWeight: 900, color: "#1e3a5f", wordBreak: "break-word", lineHeight: 1.25,
  },
  resultadoValor: { fontSize: 17, fontWeight: 700, color: "#374151" },
  resultadoDireccion: {
    fontSize: 24, fontWeight: 900, color: "#1e3a5f", lineHeight: 1.3,
  },
  avisoAprox: {
    fontSize: 13, fontWeight: 700, color: "#92400e",
    background: "#fef9c3", borderRadius: 8, padding: "5px 10px",
    marginTop: 6, display: "inline-block",
  },
  resultadoCoords: {
    fontSize: 13, color: "#6b7280", marginTop: 6, fontFamily: "monospace",
  },
  linkMapa: {
    display: "inline-block", marginTop: 10, fontSize: 15, fontWeight: 800,
    color: "#1e3a5f", textDecoration: "none",
    background: "#e0e7ff", borderRadius: 10, padding: "8px 14px",
  },
  btnPrimario: {
    flex: 1, background: "linear-gradient(135deg, #1e3a5f, #2d4f7c)",
    border: "none", borderRadius: 14, padding: "16px",
    color: "#fff", fontSize: 17, fontWeight: 800, cursor: "pointer",
  },
  btnSecundario: {
    flex: 1, background: "#fff", border: "3px solid #e2e8f0", borderRadius: 14,
    padding: "16px", fontSize: 17, fontWeight: 800, color: "#374151", cursor: "pointer",
  },
};

export default Asistenta;
